import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import { buildOfficialHabitatCheckoutHandoffUrl } from '../../../lib/officialCheckoutHandoff';

import { jsPDF } from 'jspdf';
import BuildingForm from '../components/dashboard/BuildingForm';
import PipelineBoard from '../components/dashboard/PipelineBoard';
import ComplianceCenter from '../components/dashboard/ComplianceCenter';
import ListingManager from '../components/dashboard/ListingManager';
import DashboardOverview from '../components/dashboard/DashboardOverview';
import NotificationBell from '../components/NotificationBell'; // ✅ Ajout de la cloche de notifications
import { 
  LayoutDashboard, Building, Users, ShieldCheck, ArrowLeft, 
  Settings, Key, LogOut, Star, CheckCircle, Lock, X, Loader2, 
  FileCheck, Download, FileText, Clock, MapPin, Wrench, CreditCard,
  Briefcase, PlusCircle, Search, HelpCircle, FileSpreadsheet, Calculator, Navigation, Archive, Trash2,
  FolderLock, DollarSign, TrendingUp, Eye, Calendar, Truck
} from 'lucide-react';

export default function LandlordDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard'); 
  const [profileName, setProfileName] = useState('Propriétaire');
  const [planType, setPlanType] = useState('free');
  const [showPaywall, setShowPaywall] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  const [contracts, setContracts] = useState<any[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  const [tickets, setTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [myListings, setMyListings] = useState<any[]>([]);
  const [showProTicketModal, setShowProTicketModal] = useState(false);
  const [isSubmittingProTicket, setIsSubmittingProTicket] = useState(false);
  const [proTicketForm, setProTicketForm] = useState({
    listing_id: '', category: 'plomberie', urgency: 'normale', description: ''
  });
  
  const [trackingTicket, setTrackingTicket] = useState<any>(null);

  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [isConnectingStripe, setIsConnectingStripe] = useState(false);

  const [enqueteSuccessMsg, setEnqueteSuccessMsg] = useState<string | null>(null);

  const [taxReports, setTaxReports] = useState<any[]>([]);
  const [reportYear, setReportYear] = useState<number>(new Date().getFullYear());
  const [generatingReport, setGeneratingReport] = useState(false);

  const [landlordDocs, setLandlordDocs] = useState<any[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [rentPayments, setRentPayments] = useState<any[]>([]);
  const [manualPayment, setManualPayment] = useState({ tenantName: '', amount: '', method: 'Virement', date: '' });
  
  const [calcRent, setCalcRent] = useState('');
  const [calcTaxes, setCalcTaxes] = useState('');
  const [calcRepairs, setCalcRepairs] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchProfile();
    fetchTaxReports();
    checkStripeRedirect();
    
    const channel = supabase
      .channel('landlord_maintenance_tracking')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'habitat_maintenance_tickets' },
        (payload) => {
          setTickets((currentTickets) => 
            currentTickets.map(t => t.id === payload.new.id ? { ...t, ...payload.new } : t)
          );
          setTrackingTicket((currentTracking: any) => {
            if (currentTracking && currentTracking.id === payload.new.id) {
              return { ...currentTracking, ...payload.new };
            }
            return currentTracking;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    let interval: any;
    if (activeTab === 'maintenance' || activeTab === 'suivi') {
      interval = setInterval(() => {
        fetchTicketsOnly();
      }, 10000); 
    }
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchTicketsOnly = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('habitat_maintenance_tickets')
      .select(`*, listing:habitat_listings(title, address)`)
      .eq('landlord_id', user.id)
      .order('created_at', { ascending: false });
    
    if (data) {
      setTickets(data);
      setTrackingTicket((current: any) => {
        if (current) {
          const updated = data.find(t => t.id === current.id);
          return updated ? { ...current, ...updated } : current;
        }
        return current;
      });
    }
  };

  const fetchLandlordDocs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('landlord_vault').select('*').eq('landlord_id', user.id).order('created_at', { ascending: false });
    if (data) setLandlordDocs(data);
  };

  const fetchRentPayments = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('habitat_rent_payments').select('*').eq('landlord_id', user.id).order('created_at', { ascending: false });
    if (data) setRentPayments(data);
  };

  useEffect(() => {
    if (activeTab === 'contrats') fetchContracts();
    if (activeTab === 'maintenance' || activeTab === 'finances' || activeTab === 'enquetes' || activeTab === 'suivi') {
      fetchTicketsOnly();
      fetchMyListings();
    }
    if (activeTab === 'finances') {
      fetchRentPayments();
    }
    if (activeTab === 'coffre') {
      fetchLandlordDocs();
    }
  }, [activeTab]);

  const fetchProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase.from('habitat_profiles').select('full_name, plan_type, stripe_account_id').eq('id', user.id).maybeSingle();
      if (data) {
        setProfileName(data.full_name || 'Propriétaire');
        if (data.plan_type) setPlanType(data.plan_type);
        if (data.stripe_account_id) setStripeAccountId(data.stripe_account_id);
      }
    }
  };

  const fetchTaxReports = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from('habitat_tax_reports')
      .select('*')
      .eq('landlord_id', user.id)
      .order('created_at', { ascending: false });
    if (data) setTaxReports(data);
  };

  const fetchMyListings = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('habitat_listings').select('id, title, address').eq('landlord_id', user.id);
    if (data) {
      setMyListings(data);
      if (data.length > 0 && !proTicketForm.listing_id) {
        setProTicketForm(prev => ({ ...prev, listing_id: data[0].id }));
      }
    }
  };

  const fetchContracts = async () => {
    setLoadingContracts(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('habitat_applications')
      .select(`*, listing:habitat_listings(title, address, city, monthly_rent)`)
      .eq('landlord_id', user.id)
      .order('created_at', { ascending: false });

    if (data) {
      const filteredContracts = data.filter(app => 
        app.status === 'approved' || app.tenant_signed_at || app.landlord_signed_at
      );
      setContracts(filteredContracts);
    }
    setLoadingContracts(false);
  };

  const updateTicketStatus = async (ticketId: string, newStatus: string) => {
    const { error } = await supabase
      .from('habitat_maintenance_tickets')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', ticketId);

    if (!error) {
      setTickets(tickets.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
    } else {
      alert("Erreur lors de la mise à jour du ticket.");
    }
  };

  const delegateToPros = async (ticketId: string) => {
    if (!window.confirm("Voulez-vous publier cette demande sur le portail des artisans Forfeo Pros ? Ils vous contacteront avec leurs devis.")) return;
    await updateTicketStatus(ticketId, 'searching_pro');
    alert("✅ Demande publiée avec succès ! Les professionnels de votre région ont été alertés.");
  };

  const acceptProQuote = async (ticketId: string, amount: number) => {
    if (!window.confirm(`Accepter ce devis de ${amount}$ ? Les travaux peuvent commencer.`)) return;
    await updateTicketStatus(ticketId, 'in_progress');
    alert("✅ Devis accepté ! L'artisan recevra l'adresse complète et pourra vous notifier de son départ.");
  };

  const refuseProQuote = async (ticketId: string) => {
    if (!window.confirm("Voulez-vous refuser ce devis ? La demande sera relancée aux autres artisans.")) return;
    const { error } = await supabase
      .from('habitat_maintenance_tickets')
      .update({ status: 'searching_pro', quote_amount: null, quote_message: null, pro_name: null, pro_id: null })
      .eq('id', ticketId);
    
    if (!error) {
      fetchTicketsOnly();
      alert("Devis refusé. La requête est de nouveau publique pour les autres pros.");
    }
  };

  const archiveTicket = async (ticketId: string) => {
    if (!window.confirm("Marquer cette intervention comme terminée et l'archiver dans vos dépenses comptables ?")) return;
    const { error } = await supabase
      .from('habitat_maintenance_tickets')
      .update({ is_archived: true, status: 'resolved' })
      .eq('id', ticketId);
    
    if (!error) {
      fetchTicketsOnly();
      alert("✅ Facture archivée avec succès ! Vous pouvez la retrouver dans l'onglet Finances & Impôts.");
    }
  };

  const submitProTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingProTicket(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const autoTitle = `Demande Propriétaire : ${proTicketForm.category.toUpperCase()}`;
    const { error } = await supabase.from('habitat_maintenance_tickets').insert([{
      landlord_id: user.id,
      tenant_id: null,
      listing_id: proTicketForm.listing_id,
      title: autoTitle,
      category: proTicketForm.category,
      urgency: proTicketForm.urgency,
      description: proTicketForm.description,
      status: 'searching_pro'
    }]);

    setIsSubmittingProTicket(false);

    if (!error) {
      alert("Demande envoyée au réseau Forfeo Pros !");
      setShowProTicketModal(false);
      setProTicketForm({ ...proTicketForm, description: '' });
      fetchTicketsOnly();
    } else {
      alert("Erreur lors de l'envoi : " + error.message);
    }
  };

  const handleManualPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { error } = await supabase.from('habitat_rent_payments').insert([{
      landlord_id: user.id,
      tenant_name: manualPayment.tenantName,
      amount: parseFloat(manualPayment.amount),
      payment_method: manualPayment.method,
      payment_date: manualPayment.date || new Date().toISOString(),
      status: 'succeeded'
    }]);

    if (!error) {
      alert("✅ Paiement manuel enregistré et ajouté à votre historique !");
      setManualPayment({ tenantName: '', amount: '', method: 'Virement', date: '' });
      fetchRentPayments();
    } else {
      alert("Erreur lors de l'enregistrement du paiement.");
    }
  };

  // ✅ UPLOAD VERS "landlord_vault"
  const handleUploadDoc = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setUploadingDoc(true);
    const file = e.target.files[0];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/doc_${Math.random()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage.from('landlord_vault').upload(fileName, file);
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage.from('landlord_vault').getPublicUrl(fileName);
      await supabase.from('landlord_vault').insert([{ 
        landlord_id: user.id, 
        document_type: 'general', 
        file_url: publicUrl, 
        file_name: file.name 
      }]);
      
      alert('✅ Document ajouté au coffre-fort avec succès !');
      fetchLandlordDocs(); 
    } catch (error: any) {
      alert("Erreur lors de l'upload : assurez-vous d'avoir créé le bucket 'landlord_vault' en public. (" + error.message + ")");
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDoc = async (id: string) => {
    if (!window.confirm('Supprimer définitivement ce document de votre coffre-fort ?')) return;
    await supabase.from('landlord_vault').delete().eq('id', id);
    fetchLandlordDocs();
  };

  const handleGenerateReport = async (year: number) => {
    setGeneratingReport(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const yearTickets = tickets.filter(t => new Date(t.created_at).getFullYear() === year && t.is_archived);

      const doc = new jsPDF();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text(`Forfeo Habitat - Rapport Fiscal ${year}`, 20, 30);

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Propriétaire : ${profileName}`, 20, 45);
      doc.text(`Date d'émission : ${new Date().toLocaleDateString()}`, 20, 52);

      doc.setLineWidth(0.5);
      doc.line(20, 60, 190, 60);

      doc.setFont("helvetica", "bold");
      doc.text("Sommaire des Dépenses de Maintenance & Améliorations :", 20, 75);
      doc.setFont("helvetica", "normal");
      
      let yPos = 85;
      let totalAmount = 0;

      if (yearTickets.length === 0) {
        doc.text("Aucune dépense enregistrée via la plateforme cette année-là.", 20, yPos);
      } else {
        yearTickets.forEach((t, i) => {
           const amount = t.quote_amount ? parseFloat(t.quote_amount).toFixed(2) : '0.00';
           totalAmount += t.quote_amount || 0;
           
           doc.text(`- ${new Date(t.updated_at || t.created_at).toLocaleDateString()} | ${t.category.toUpperCase()}`, 20, yPos);
           doc.text(`${amount} $`, 170, yPos, { align: 'right' }); 
           
           doc.setFontSize(9);
           doc.setTextColor(100);
           doc.text(`  Prestataire: ${t.pro_name || 'Inconnu'} (${t.listing?.title || 'Logement masqué'})`, 20, yPos + 5);
           doc.setFontSize(12);
           doc.setTextColor(0);
           yPos += 12;

           if (yPos > 270) {
             doc.addPage();
             yPos = 20;
           }
        });

        doc.setLineWidth(0.2);
        doc.line(20, yPos + 5, 190, yPos + 5);
        doc.setFont("helvetica", "bold");
        doc.text("TOTAL DES DÉPENSES :", 120, yPos + 15);
        doc.text(`${totalAmount.toFixed(2)} $`, 170, yPos + 15, { align: 'right' });
      }

      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text("Ce document est un récapitulatif généré par Forfeo Explore à des fins comptables.", 20, 285);

      const pdfBlob = doc.output('blob');
      const fileName = `${user.id}/rapport_fiscal_${year}_${Date.now()}.pdf`;
      
      const { error: uploadError } = await supabase.storage.from('landlord_vault').upload(fileName, pdfBlob, {
        contentType: 'application/pdf'
      });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('landlord_vault').getPublicUrl(fileName);

      const { error: dbError } = await supabase.from('habitat_tax_reports').insert([{
        landlord_id: user.id,
        year: year,
        file_url: publicUrl
      }]);

      if (dbError) throw dbError;

      alert(`✅ Rapport de l'année ${year} généré et archivé avec succès !`);
      
      fetchTaxReports();
      window.open(publicUrl, '_blank');

    } catch (err: any) {
      console.error(err);
      alert("Erreur lors de la génération du rapport : " + err.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const checkStripeRedirect = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    
    if (queryParams.get('connect') === 'success') {
      fetchProfile();
      window.history.replaceState(null, '', window.location.pathname);
      alert("Votre compte bancaire a été connecté avec succès ! Vous êtes prêt à recevoir vos loyers.");
    } 
    else if (queryParams.get('success') === 'true') {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('habitat_profiles').update({ plan_type: 'pro' }).eq('id', user.id);
        setPlanType('pro');
        setShowPaywall(false);
        import('canvas-confetti').then((confetti) => {
          confetti.default({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ['#14b8a6', '#0ea5e9', '#3b82f6'] });
        });
        window.history.replaceState(null, '', window.location.pathname);
      }
    } 
    else if (queryParams.get('enquete') === 'success') {
      setActiveTab('enquetes');
      setEnqueteSuccessMsg("Paiement réussi ! L'enquête de conciergerie a été initiée.");
      window.history.replaceState(null, '', window.location.pathname);
      setTimeout(() => setEnqueteSuccessMsg(null), 8000);
    }
    else if (queryParams.get('canceled') === 'true') {
      setShowPaywall(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const upgradeToPro = async () => {
    setIsProcessingPayment(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      try {
        const returnUrl = window.location.origin + window.location.pathname;
        const { data, error } = await supabase.functions.invoke('stripe-checkout', {
          body: { userId: user.id, email: user.email, returnUrl: returnUrl }
        });
        if (error) throw error;
        if (data?.url) window.location.href = data.url;
      } catch (err) {
        alert("Une erreur est survenue lors de la connexion à Stripe.");
        setIsProcessingPayment(false);
      }
    }
  };

  const connectBank = async () => {
    setIsConnectingStripe(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    try {
      const returnUrl = window.location.origin + window.location.pathname;
      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        body: { landlord_id: user.id, user_type: 'landlord', return_url: returnUrl }
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      console.error(err);
      alert("Erreur de connexion à Stripe.");
      setIsConnectingStripe(false);
    }
  };

  const archivedTickets = tickets.filter(t => t.is_archived && t.quote_amount);
  const groupedInvoices = archivedTickets.reduce((acc: any, ticket: any) => {
    const date = new Date(ticket.updated_at || ticket.created_at);
    const monthYear = date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
    const capitalizedMonth = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
    
    if (!acc[capitalizedMonth]) acc[capitalizedMonth] = { total: 0, items: [] };
    acc[capitalizedMonth].items.push(ticket);
    acc[capitalizedMonth].total += ticket.quote_amount;
    return acc;
  }, {});

  const activeTrackingTickets = tickets.filter(t => (t.status === 'in_progress' || t.status === 'resolved') && !t.is_archived);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col md:flex-row font-sans">
      <aside className="w-full md:w-[280px] bg-[#0B1120] text-slate-300 flex flex-col md:h-screen md:sticky md:top-0 shadow-2xl z-50 overflow-hidden">
        
        {/* ✅ DÉPLACEMENT DE LA CLOCHE EN HAUT DE L'ÉCRAN ICI */}
        <div className="p-6 border-b border-slate-800 flex flex-col gap-4 shrink-0">
          <div className="flex justify-between items-center w-full">
            <h2 className="text-2xl font-black text-white">Forfeo <span className="text-teal-400 font-light">Habitat</span></h2>
            <div className="bg-white/10 rounded-full border border-white/20 p-1 flex items-center justify-center">
              <NotificationBell />
            </div>
          </div>
          <button onClick={() => navigate('/')} className="text-xs bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors w-full text-center">Retour au site</button>
        </div>

        <nav className="flex-1 flex md:flex-col p-4 gap-2 overflow-y-auto overflow-x-auto hide-scrollbar">
          <button onClick={() => setActiveTab('dashboard')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='dashboard'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><LayoutDashboard size={20}/> Accueil</button>
          <button onClick={() => setActiveTab('immeubles')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='immeubles'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><Building size={20}/> Immeubles</button>
          <button onClick={() => setActiveTab('annonces')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='annonces'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><Key size={20}/> Annonces</button>
          <button onClick={() => setActiveTab('pipeline')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='pipeline'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><Users size={20}/> Candidatures</button>
          <button onClick={() => setActiveTab('enquetes')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='enquetes'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><Search size={20}/> Enquêtes Crédit</button>
          <button onClick={() => setActiveTab('contrats')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='contrats'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><FileCheck size={20}/> Mes Contrats</button>
          <button onClick={() => setActiveTab('finances')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='finances'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><CreditCard size={20}/> Finances & Impôts</button>
          <button onClick={() => setActiveTab('maintenance')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='maintenance'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><Wrench size={20}/> Maintenance</button>
          <button onClick={() => setActiveTab('suivi')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='suivi'?'bg-green-500/10 text-green-400':'hover:bg-white/5'}`}><Truck size={20}/> Suivi GPS</button>
          <button onClick={() => setActiveTab('coffre')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='coffre'?'bg-teal-500/10 text-teal-400':'hover:bg-white/5'}`}><FolderLock size={20}/> Coffre-fort</button>
          <button onClick={() => setActiveTab('conformite')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='conformite'?'bg-amber-500/10 text-amber-400':'hover:bg-white/5'}`}><ShieldCheck size={20}/> Centre TAL</button>
          <button onClick={() => setActiveTab('faq')} className={`flex gap-3 p-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='faq'?'bg-purple-500/10 text-purple-400':'hover:bg-white/5'}`}><HelpCircle size={20}/> FAQ / Aide</button>
        </nav>
        
        <div className="mt-auto p-4 border-t border-slate-800/50 shrink-0 bg-[#0B1120] pb-24 md:pb-20">
          <div className="px-3 py-3 mb-3 bg-white/5 rounded-2xl">
            <p className="text-[10px] font-black uppercase text-slate-500 mb-1 tracking-widest">Connecté</p>
            <p className="text-sm font-bold text-white truncate">{profileName}</p>
          </div>
          <button onClick={handleLogout} className="text-red-400 text-xs font-bold uppercase flex gap-2 p-3 hover:bg-red-500/10 rounded-xl w-full justify-center transition-colors border border-red-500/20"><LogOut size={16}/> Déconnexion</button>
        </div>
      </aside>

      <main className="flex-1 relative overflow-y-auto h-screen bg-slate-50">
        {planType === 'free' && (
          <div className="bg-gradient-to-r from-teal-500 to-blue-500 text-white px-6 py-3 flex justify-between items-center sticky top-0 z-40 shadow-lg">
            <p className="text-sm font-bold flex items-center gap-2"><Star size={16}/> Vous utilisez le forfait gratuit (Limité à 1 immeuble).</p>
            <button onClick={() => setShowPaywall(true)} className="bg-white text-slate-900 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest hover:scale-105 transition-transform">Débloquer Pro</button>
          </div>
        )}

        <div className="p-8 max-w-[1600px] mx-auto pb-24">
          {activeTab === 'dashboard' && <DashboardOverview />}
          {activeTab === 'immeubles' && <BuildingForm />}
          {activeTab === 'annonces' && <ListingManager />}
          {activeTab === 'pipeline' && <PipelineBoard />}
          {activeTab === 'conformite' && <ComplianceCenter />}

          {activeTab === 'suivi' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-black text-slate-900">Suivi des Interventions</h2>
                  <p className="text-slate-500 font-medium">Suivez l'arrivée de l'artisan en direct sur la carte.</p>
                </div>
              </div>

              {activeTrackingTickets.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-[3rem] p-24 text-center">
                  <Truck size={40} className="mx-auto text-slate-200 mb-4" />
                  <h3 className="text-xl font-black text-slate-800 mb-2">Aucune intervention en cours</h3>
                  <p className="text-slate-400 font-medium">Le suivi GPS s'affichera ici dès que vous aurez autorisé un artisan pour des travaux.</p>
                </div>
              ) : (
                <div className="grid gap-8">
                  {activeTrackingTickets.map(ticket => (
                    <div key={ticket.id} className="bg-white rounded-[2.5rem] shadow-xl border border-slate-200 overflow-hidden">
                      <div className="p-6 md:p-8 bg-slate-50 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center shrink-0 border border-green-200">
                            <Truck size={28}/>
                          </div>
                          <div>
                            <h3 className="text-xl font-black text-slate-900">{ticket.pro_name || 'Artisan Forfeo'}</h3>
                            <p className="text-sm font-bold text-slate-500 flex items-center gap-1"><MapPin size={14}/> Destination : {ticket.listing?.address}</p>
                          </div>
                        </div>
                        <div className="text-right w-full md:w-auto">
                          <span className={`inline-block px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border ${ticket.tracking_status === 'en_route' ? 'bg-green-100 text-green-700 border-green-200 animate-pulse' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                            {ticket.tracking_status === 'en_route' ? 'En Route 📍' : 'En préparation'}
                          </span>
                        </div>
                      </div>

                      <div className="h-[400px] md:h-[500px] w-full bg-slate-100 relative">
                        {ticket.tracking_status === 'en_route' && ticket.pro_lat && ticket.pro_lng ? (
                          <iframe 
                            width="100%" 
                            height="100%" 
                            frameBorder="0" 
                            scrolling="no" 
                            marginHeight={0} 
                            marginWidth={0} 
                            src={`https://www.openstreetmap.org/export/embed.html?bbox=${ticket.pro_lng - 0.01}%2C${ticket.pro_lat - 0.01}%2C${ticket.pro_lng + 0.01}%2C${ticket.pro_lat + 0.01}&layer=mapnik&marker=${ticket.pro_lat}%2C${ticket.pro_lng}`} 
                            style={{ border: 0 }}
                          ></iframe>
                        ) : (
                          <div className="flex items-center justify-center h-full flex-col text-slate-400 p-8 text-center">
                            <Loader2 className="animate-spin w-12 h-12 mb-4 text-slate-300"/>
                            <p className="font-black text-xl text-slate-500 mb-2">En attente du départ de l'artisan...</p>
                            <p className="text-sm font-medium">La carte s'affichera automatiquement ici dès que {ticket.pro_name || "l'artisan"} aura activé son GPS.</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-6 bg-white border-t border-slate-100">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                          <p className="text-sm font-bold text-slate-600">Intervention : <span className="text-slate-900">{ticket.category.toUpperCase()}</span> - Devis accepté : <strong className="text-lg">{ticket.quote_amount?.toFixed(2)} $</strong></p>
                          <button 
                            onClick={() => archiveTicket(ticket.id)}
                            className="w-full md:w-auto bg-slate-900 text-white px-8 py-4 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-teal-500 transition-colors shadow-lg"
                          >
                            <Archive size={16} className="inline mr-2"/> Payer & Archiver
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'coffre' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-black text-slate-900">Coffre-fort Sécurisé</h2>
                  <p className="text-slate-500 font-medium">Stockez vos baux, règlements et correspondances privées en sécurité.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center h-[350px]">
                  <input type="file" onChange={handleUploadDoc} className="hidden" id="upload-doc" accept=".pdf,image/*" />
                  <FolderLock className="text-teal-500 mb-4" size={56} />
                  <h3 className="font-black text-xl mb-2">Nouveau Document</h3>
                  <p className="text-sm text-slate-500 mb-8">Format PDF, JPG ou PNG</p>
                  <button onClick={() => document.getElementById('upload-doc')?.click()} disabled={uploadingDoc} className="w-full bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-teal-600 transition-colors shadow-lg">
                    {uploadingDoc ? 'Envoi sécurisé...' : 'Téléverser un fichier'}
                  </button>
                </div>

                <div className="md:col-span-2 bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[350px]">
                  <div className="p-6 bg-slate-50 border-b border-slate-100 font-black text-slate-800 flex justify-between items-center">
                    <div className="flex items-center gap-3"><FileCheck size={20} className="text-teal-500"/> Fichiers archivés</div>
                    <span className="bg-slate-200 text-slate-600 px-3 py-1 rounded-full text-xs">{landlordDocs.length} Fichiers</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {landlordDocs.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-slate-400">
                        <FileText size={40} className="mb-3 opacity-30"/>
                        <p className="text-base font-bold">Votre coffre-fort est vide.</p>
                      </div>
                    ) : (
                      landlordDocs.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-4 border border-slate-100 rounded-2xl hover:bg-slate-50 transition-colors shadow-sm">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center shrink-0"><FileText size={20}/></div>
                            <div>
                              <p className="font-bold text-slate-800 truncate max-w-[200px] sm:max-w-[300px]">{doc.file_name}</p>
                              <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">Ajouté le {new Date(doc.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <a href={doc.file_url} target="_blank" rel="noreferrer" className="p-3 bg-white border border-slate-200 rounded-xl hover:text-teal-600 shadow-sm hover:border-teal-200 transition"><Eye size={16}/></a>
                            <button onClick={() => deleteDoc(doc.id)} className="p-3 bg-white border border-slate-200 rounded-xl hover:text-red-600 shadow-sm hover:border-red-200 transition"><Trash2 size={16}/></button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'enquetes' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-black text-slate-900">Enquêtes de Crédit</h2>
                  <p className="text-slate-500 font-medium">Vérifiez les antécédents et le crédit de vos futurs locataires en conformité avec la Loi 25.</p>
                </div>
              </div>

              {enqueteSuccessMsg && (
                <div className="mb-6 bg-green-50 text-green-700 p-4 rounded-2xl border border-green-200 flex items-center gap-3 font-bold animate-in zoom-in duration-300">
                  <CheckCircle size={20} className="text-green-500" />
                  {enqueteSuccessMsg}
                </div>
              )}

              {/* ✅ PRIX ET TEXTES MIS À JOUR POUR LE CONCIERGE (54.99$) */}
              <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm max-w-xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                    <Search size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-900">Nouvelle enquête</h3>
                    <p className="text-sm text-slate-500">Propulsé par nos partenaires accrédités Equifax</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                   <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Courriel du candidat locataire</label>
                      <input 
                        type="email" 
                        id="tenantEmailInput"
                        placeholder="candidat@email.com" 
                        className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none" 
                      />
                      <p className="text-[10px] text-slate-400 mt-2 font-medium">Un lien de vérification sécurisé sera envoyé à cette adresse.</p>
                   </div>

                   <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Logement visé (Optionnel)</label>
                      <select 
                        id="listingSelectInput"
                        className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none cursor-pointer appearance-none"
                      >
                        <option value="">Sélectionner une annonce...</option>
                        {myListings.map(l => (
                          <option key={l.id} value={l.id}>{l.title} - {l.address}</option>
                        ))}
                      </select>
                   </div>
                </div>

                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 mt-6">
                  <h4 className="font-black text-slate-900 mb-4 text-sm uppercase tracking-widest">Détail de la facturation</h4>
                  
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between text-slate-600">
                      <span>Enquête de pré-location complète</span>
                      <span className="font-bold">50,00 $</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Frais de plateforme / Taxes</span>
                      <span className="font-bold">4,99 $</span>
                    </div>
                    <div className="flex justify-between text-slate-900 border-t border-slate-200 pt-2 mt-2">
                      <span className="font-black">Total à payer</span>
                      <span className="font-black text-lg">54,99 $</span>
                    </div>
                  </div>

                  <button 
                    onClick={async () => {
                        const emailInput =
                          (
                            document.getElementById(
                              'tenantEmailInput',
                            ) as HTMLInputElement | null
                          )?.value.trim() || '';

                        const listingInput =
                          (
                            document.getElementById(
                              'listingSelectInput',
                            ) as HTMLSelectElement | null
                          )?.value.trim() || '';

                        if (!emailInput) {
                          alert(
                            "Veuillez entrer l'adresse courriel du locataire.",
                          );
                          return;
                        }

                        setIsProcessingPayment(true);

                        try {
                          const {
                            data: sessionData,
                            error: sessionError,
                          } =
                            await supabase.auth.getSession();

                          if (
                            sessionError ||
                            !sessionData.session
                          ) {
                            throw new Error(
                              'Session expirée. Veuillez vous reconnecter.',
                            );
                          }

                          const {
                            data: intentResult,
                            error: intentError,
                          } = await supabase.rpc(
                            'create_habitat_credit_screening_intent',
                            {
                              p_tenant_email:
                                emailInput,
                              p_listing_id:
                                listingInput || null,
                            },
                          );

                          if (intentError) {
                            throw intentError;
                          }

                          const checkoutUrl =
                            await buildOfficialHabitatCheckoutHandoffUrl(
                              'habitat_credit_screening',
                              intentResult,
                            );

                          window.location.assign(
                            checkoutUrl,
                          );
                        } catch (err: unknown) {
                          const message =
                            err instanceof Error
                              ? err.message
                              : 'Erreur inconnue';

                          alert(
                            'Erreur lors de la création de la transaction : '
                              + message,
                          );
                        } finally {
                          setIsProcessingPayment(false);
                        }
                      }}
                      disabled={isProcessingPayment}
                    className="w-full bg-slate-900 text-white font-black uppercase tracking-widest py-4 rounded-xl hover:bg-teal-600 transition-colors shadow-lg mb-4 flex justify-center items-center gap-2 disabled:opacity-70"
                  >
                    {isProcessingPayment ? <Loader2 className="animate-spin" size={18} /> : 'Payer 54,99 $ et envoyer la demande'}
                  </button>

                  <div className="text-[9px] text-slate-400 leading-tight text-justify bg-white p-3 rounded-lg border border-slate-100">
                    <strong>Avis légal :</strong> Forfeo Explore agit exclusivement à titre d'intermédiaire technologique facilitant la vérification de crédit auprès d'agences d'évaluation tierces certifiées. Le montant facturé correspond au coût réel de la vérification par l'agence tierce, majoré d'un frais administratif perçu par Forfeo. Forfeo décline toute responsabilité quant à l'exactitude des informations du rapport ou aux décisions de location. Conformément à la Loi 25, la vérification requiert le consentement du locataire.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contrats' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-black text-slate-900">Archives des Contrats</h2>
                  <p className="text-slate-500 font-medium">Consultez et gérez vos baux scellés numériquement.</p>
                </div>
                <div className="bg-teal-50 text-teal-700 px-5 py-2 rounded-full text-xs font-black uppercase border border-teal-100 shadow-sm hidden md:block">
                  {contracts.length} Contrats enregistrés
                </div>
              </div>

              {loadingContracts ? (
                <div className="py-32 text-center animate-pulse text-slate-300 font-black tracking-widest uppercase">Chargement des baux...</div>
              ) : contracts.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-[3rem] p-24 text-center">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <FileText size={40} className="text-slate-200" />
                  </div>
                  <h3 className="text-xl font-black text-slate-800 mb-2">Aucun bail généré</h3>
                  <p className="text-slate-400 font-medium">Dès qu'un candidat est approuvé, son bail apparaîtra ici.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {contracts.map(app => {
                    const isFullySigned = app.tenant_signed_at && app.landlord_signed_at;
                    return (
                      <div key={app.id} className="bg-white rounded-[2rem] border border-slate-200 p-6 md:p-8 flex flex-col md:flex-row justify-between items-center gap-6 hover:shadow-xl transition-all group hover:border-teal-200">
                        <div className="flex items-center gap-6 flex-1">
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border shadow-inner transition-colors ${isFullySigned ? 'bg-teal-500 text-white' : 'bg-slate-50 text-slate-300'}`}>
                            <FileText size={32} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-xl font-black text-slate-900 tracking-tight">{app.listing?.address || "Adresse non spécifiée"}</h3>
                              {isFullySigned && <ShieldCheck size={20} className="text-teal-500" />}
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              <p className="text-sm font-bold text-slate-500 flex items-center gap-1.5"><Users size={14}/> Locataire : {app.tenant_name}</p>
                              <div className="hidden md:block w-1.5 h-1.5 rounded-full bg-slate-200"></div>
                              <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg ${isFullySigned ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}>
                                {isFullySigned ? 'Contrat Scellé' : 'Attente de signatures'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 w-full md:w-auto border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-8">
                          <button onClick={() => navigate(`/habitat/lease/${app.id}`)} className="flex-1 md:flex-none bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-teal-600 transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-900/10"><Download size={16} /> Gérer / Signer</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'finances' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-3xl font-black text-slate-900">Finances & Impôts</h2>
                  <p className="text-slate-500 font-medium">Gérez vos loyers, vos factures et générez vos rapports pour Revenu Québec.</p>
                </div>
                <button 
                  onClick={() => handleGenerateReport(reportYear)} 
                  disabled={generatingReport}
                  className="bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-teal-500 transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {generatingReport ? <Loader2 className="animate-spin" size={16} /> : <FileSpreadsheet size={16} />} 
                  Rapport Fiscal {reportYear}
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm flex flex-col justify-center">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center">
                      <CreditCard size={24} />
                    </div>
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${stripeAccountId ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {stripeAccountId ? 'Connecté' : 'Non configuré'}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-slate-900 mb-2">Perception des loyers en ligne</h3>
                  <p className="text-slate-500 text-sm mb-6">Liez votre compte bancaire via Stripe pour permettre à vos locataires de payer directement depuis leur espace.</p>
                  <button onClick={connectBank} disabled={isConnectingStripe} className="w-full bg-slate-100 text-slate-700 py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-70">
                    {isConnectingStripe ? <Loader2 className="animate-spin" size={16} /> : <Settings size={16} />} Gérer mon compte Stripe
                  </button>
                </div>

                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2rem] border border-slate-700 p-8 shadow-xl text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/20 blur-3xl rounded-full -mr-10 -mt-10 pointer-events-none"></div>
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-center gap-3 mb-6">
                      <Archive className="text-teal-400" size={24}/>
                      <h3 className="text-xl font-black">Archives Fiscales</h3>
                    </div>
                    <div className="flex-1 space-y-2 max-h-[140px] overflow-y-auto pr-2 custom-scrollbar">
                      {taxReports.length === 0 ? (
                        <p className="text-sm text-slate-400 italic mt-4">Aucun rapport généré pour le moment.</p>
                      ) : (
                        taxReports.map(report => (
                          <div key={report.id} className="flex items-center justify-between bg-white/5 p-3 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                            <div className="flex items-center gap-3">
                              <FileText size={16} className="text-teal-400" />
                              <span className="text-sm font-bold text-slate-200">Rapport_{report.year}.pdf</span>
                            </div>
                            <button onClick={() => window.open(report.file_url, '_blank')} className="text-white hover:text-teal-300 p-2">
                              <Download size={14} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-6 mt-10">
                  <div className="w-10 h-10 bg-green-100 text-green-600 rounded-xl flex items-center justify-center"><DollarSign size={20}/></div>
                  <h3 className="text-2xl font-black text-slate-900">Registre des Loyers</h3>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col justify-center">
                    <h4 className="font-black text-slate-800 mb-4 flex items-center gap-2"><PlusCircle size={16} className="text-green-500"/> Saisir un paiement manuel</h4>
                    <form onSubmit={handleManualPayment} className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Locataire</label>
                        <input required value={manualPayment.tenantName} onChange={e => setManualPayment({...manualPayment, tenantName: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-green-500" placeholder="Nom du locataire"/>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Montant ($)</label>
                          <input type="number" required value={manualPayment.amount} onChange={e => setManualPayment({...manualPayment, amount: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-green-500" placeholder="0.00"/>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Méthode</label>
                          <select value={manualPayment.method} onChange={e => setManualPayment({...manualPayment, method: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none cursor-pointer focus:border-green-500">
                            <option value="Virement">Virement</option>
                            <option value="Comptant">Comptant</option>
                            <option value="Chèque">Chèque</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Date</label>
                        <input type="date" required value={manualPayment.date} onChange={e => setManualPayment({...manualPayment, date: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-green-500"/>
                      </div>
                      <button type="submit" className="w-full bg-green-500 text-white py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-green-600 transition shadow-md">Enregistrer le loyer</button>
                    </form>
                  </div>

                  <div className="lg:col-span-2 bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[380px]">
                     <div className="p-5 bg-slate-50 border-b border-slate-100 font-black text-slate-700 flex justify-between items-center">
                       <span>Historique des encaissements</span>
                       <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">{rentPayments.length} paiements</span>
                     </div>
                     <div className="flex-1 overflow-y-auto p-4">
                       {rentPayments.length === 0 ? (
                         <div className="h-full flex flex-col items-center justify-center text-slate-400">
                           <CreditCard size={40} className="mb-3 opacity-30"/>
                           <p className="text-sm font-bold">Aucun loyer enregistré.</p>
                         </div>
                       ) : (
                         <table className="w-full text-left border-collapse">
                           <tbody>
                             {rentPayments.map(rp => (
                               <tr key={rp.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                 <td className="py-4 pl-2">
                                   <p className="font-bold text-slate-900 text-base">{rp.tenant_name}</p>
                                   <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{new Date(rp.payment_date || rp.created_at).toLocaleDateString()}</p>
                                 </td>
                                 <td className="py-4 text-center">
                                   <span className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-slate-200">{rp.payment_method}</span>
                                 </td>
                                 <td className="py-4 pr-2 text-right">
                                   <p className="font-black text-green-600 text-lg">{Number(rp.amount).toFixed(2)} $</p>
                                 </td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       )}
                     </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-6 mt-12">
                  <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center"><Calculator size={20}/></div>
                  <h3 className="text-2xl font-black text-slate-900">Dépenses de Maintenance (Pros)</h3>
                </div>

                {Object.keys(groupedInvoices).length === 0 ? (
                  <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-16 text-center text-slate-400">
                    <Archive size={48} className="mx-auto mb-4 opacity-30"/>
                    <p className="font-bold text-lg">Aucune facture d'artisan archivée.</p>
                    <p className="text-sm mt-2 max-w-md mx-auto">Acceptez un devis dans le Centre de Maintenance et cliquez sur "Archiver" à la fin des travaux pour qu'il s'ajoute automatiquement à votre comptabilité.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.keys(groupedInvoices).map((monthYear) => {
                      const monthData = groupedInvoices[monthYear];
                      return (
                        <div key={monthYear} className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm">
                          <div className="bg-slate-50 p-6 border-b border-slate-200 flex justify-between items-center">
                            <h4 className="font-black text-xl text-slate-800 capitalize flex items-center gap-3"><Calendar size={20} className="text-teal-500"/> {monthYear}</h4>
                            <div className="text-right">
                              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Total mensuel</p>
                              <p className="text-2xl font-black text-slate-900">{monthData.total.toFixed(2)} $</p>
                            </div>
                          </div>
                          <div className="divide-y divide-slate-100">
                            {monthData.items.map((invoice: any) => (
                              <div key={invoice.id} className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 hover:bg-slate-50 transition-colors">
                                <div className="flex items-center gap-5">
                                  <div className="w-14 h-14 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center shrink-0">
                                    <Briefcase size={24}/>
                                  </div>
                                  <div>
                                    <p className="font-black text-slate-900 text-lg">{invoice.pro_name || 'Artisan Forfeo'}</p>
                                    <p className="text-sm text-slate-500 font-medium">Intervention : {invoice.category.toUpperCase()} - {invoice.listing?.title}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                                  <div className="text-right">
                                    <p className="font-black text-2xl text-slate-900">{invoice.quote_amount.toFixed(2)} $</p>
                                    <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest mt-1">Archivé le {new Date(invoice.updated_at).toLocaleDateString()}</p>
                                  </div>
                                  <span className="hidden md:flex bg-green-100 text-green-700 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest items-center gap-2 border border-green-200">
                                    <CheckCircle size={14}/> Payé & Classé
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-12 bg-gradient-to-br from-indigo-900 to-slate-900 rounded-[2.5rem] border border-indigo-800 p-8 md:p-12 shadow-2xl text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 blur-[100px] rounded-full pointer-events-none"></div>
                <div className="relative z-10 flex flex-col lg:flex-row gap-10 items-center">
                  <div className="lg:w-1/3">
                    <div className="w-14 h-14 bg-indigo-500/20 text-indigo-400 rounded-2xl flex items-center justify-center mb-6 border border-indigo-500/30">
                      <TrendingUp size={28}/>
                    </div>
                    <h3 className="text-3xl font-black mb-4 tracking-tight">Calculateur de Hausse (TAL)</h3>
                    <p className="text-indigo-200 text-sm leading-relaxed font-medium">Estimez rapidement l'augmentation de loyer permise en fonction de vos dépenses d'opération. Outil indicatif basé sur les critères généraux du Tribunal.</p>
                  </div>
                  <div className="lg:w-2/3 w-full bg-white/5 p-8 rounded-[2rem] border border-white/10 backdrop-blur-md">
                    <div className="grid md:grid-cols-2 gap-5 mb-6">
                      <div>
                        <label className="block text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Loyer Actuel Mensuel ($)</label>
                        <input type="number" value={calcRent} onChange={e => setCalcRent(e.target.value)} className="w-full p-4 bg-slate-900/50 border border-indigo-500/30 rounded-xl text-white font-bold text-lg outline-none focus:border-indigo-400" placeholder="Ex: 1200"/>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Hausse Taxes & Assurances ($)</label>
                        <input type="number" value={calcTaxes} onChange={e => setCalcTaxes(e.target.value)} className="w-full p-4 bg-slate-900/50 border border-indigo-500/30 rounded-xl text-white font-bold text-lg outline-none focus:border-indigo-400" placeholder="Sur la facture annuelle"/>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Travaux majeurs de l'année ($)</label>
                        <input type="number" value={calcRepairs} onChange={e => setCalcRepairs(e.target.value)} className="w-full p-4 bg-slate-900/50 border border-indigo-500/30 rounded-xl text-white font-bold text-lg outline-none focus:border-indigo-400" placeholder="Coût total des réparations capitales"/>
                      </div>
                    </div>
                    <div className="bg-indigo-600 p-6 rounded-2xl border border-indigo-500 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-inner">
                      <div className="text-center sm:text-left">
                        <p className="text-[12px] font-black uppercase tracking-widest text-indigo-200">Nouveau Loyer Estimé</p>
                        <p className="text-[10px] text-indigo-300 mt-1 font-bold">* Estimation (4% de base + amortissement)</p>
                      </div>
                      <p className="text-4xl font-black text-white bg-indigo-900/40 px-6 py-2 rounded-xl">
                        {((parseFloat(calcRent) || 0) * 1.04 + (parseFloat(calcTaxes) || 0)/12 + (parseFloat(calcRepairs) || 0)*0.02/12).toFixed(2)} $
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {activeTab === 'maintenance' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
                <div>
                  <h2 className="text-3xl font-black text-slate-900">Centre de Maintenance</h2>
                  <p className="text-slate-500 font-medium">Gérez les demandes, acceptez les devis et suivez l'arrivée des pros.</p>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowProTicketModal(true)}
                    className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg hover:bg-teal-600 transition-colors flex items-center gap-2"
                  >
                    <Briefcase size={16}/> Demander un devis Pro
                  </button>
                </div>
              </div>

              {loadingTickets ? (
                <div className="py-32 text-center animate-pulse text-slate-300 font-black tracking-widest uppercase">Chargement des tickets...</div>
              ) : tickets.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-[3rem] p-24 text-center">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Wrench size={40} className="text-slate-200" />
                  </div>
                  <h3 className="text-xl font-black text-slate-800 mb-2">Aucun problème signalé</h3>
                  <p className="text-slate-400 font-medium">Vos logements sont en parfait état !</p>
                </div>
              ) : (
                <div className="grid gap-6">
                  {tickets.map(ticket => {
                    if (ticket.is_archived) return null;

                    return (
                      <div key={ticket.id} className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row gap-6 justify-between relative overflow-hidden">
                        
                        <div className="flex gap-6 flex-1">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border shrink-0 ${ticket.status === 'open' ? 'bg-amber-100 text-amber-600 border-amber-200' : ticket.status === 'searching_pro' ? 'bg-purple-100 text-purple-600 border-purple-200' : ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-600 border-blue-200' : 'bg-green-100 text-green-600 border-green-200'}`}>
                            {ticket.status === 'resolved' ? <CheckCircle size={28} /> : ticket.status === 'searching_pro' ? <Briefcase size={28}/> : <Wrench size={28} />}
                          </div>
                          <div className="w-full">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-black uppercase tracking-widest text-slate-500">{ticket.category}</span>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${ticket.urgency === 'urgente' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                                {ticket.urgency}
                              </span>
                            </div>
                            <h3 className="text-lg font-black text-slate-900 mb-2">{ticket.description}</h3>
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-slate-500 flex items-center gap-2"><MapPin size={14}/> {ticket.listing?.address || 'Adresse inconnue'}</p>
                              <p className="text-sm font-bold text-slate-500 flex items-center gap-2">
                                <Users size={14}/> {ticket.tenant_id ? 'Signalé par votre locataire' : 'Créé par vous-même'}
                              </p>
                            </div>
                            
                            {ticket.status === 'open' && (
                              <button 
                                onClick={() => delegateToPros(ticket.id)} 
                                className="mt-4 bg-amber-100 text-amber-800 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-amber-200 transition-colors flex items-center gap-2 border border-amber-200 shadow-sm"
                              >
                                <Briefcase size={14}/> Demander des devis (Forfeo Pros)
                              </button>
                            )}

                            {ticket.status === 'searching_pro' && ticket.quote_amount && (
                              <div className="mt-4 bg-purple-50 p-5 rounded-2xl border border-purple-200 shadow-sm">
                                <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-purple-100 mb-4">
                                  <div><p className="text-sm font-bold text-slate-900">{ticket.pro_name || 'Artisan'}</p></div>
                                  <p className="font-black text-slate-900 text-2xl">{ticket.quote_amount.toFixed(2)} $</p>
                                </div>
                                <div className="flex gap-3">
                                  <button onClick={() => acceptProQuote(ticket.id, ticket.quote_amount)} className="flex-1 bg-purple-600 text-white text-xs font-black uppercase py-3 rounded-xl hover:bg-purple-700">Accepter</button>
                                  <button onClick={() => refuseProQuote(ticket.id)} className="bg-white text-red-600 border border-red-200 text-xs font-black uppercase px-4 py-3 rounded-xl hover:bg-red-50">Refuser</button>
                                </div>
                              </div>
                            )}

                            {(ticket.status === 'in_progress' || ticket.status === 'resolved') && ticket.quote_amount && !ticket.is_archived && (
                              <div className="mt-6 bg-blue-50 p-5 rounded-2xl border border-blue-200">
                                <p className="text-sm font-bold text-blue-900 mb-3">Intervention acceptée : {ticket.quote_amount.toFixed(2)} $</p>
                                <button onClick={() => setActiveTab('suivi')} className="w-full bg-slate-900 text-white px-5 py-4 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-teal-500 shadow-lg">
                                  <Navigation size={18} className="inline mr-2"/> Voir le Suivi GPS
                                </button>
                              </div>
                            )}

                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'faq' && (
             <div className="animate-in fade-in slide-in-from-bottom-4 max-w-4xl mx-auto">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4"><HelpCircle size={32}/></div>
                  <h2 className="text-3xl font-black text-slate-900">Foire aux questions</h2>
                  <p className="text-slate-500 font-medium">Tout ce qu'il faut savoir pour gérer vos biens immobiliers au Québec.</p>
                </div>
                <div className="space-y-4">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-black text-slate-900 mb-2">Comment fonctionne l'enquête de crédit ?</h4>
                    <p className="text-slate-600 text-sm">Pour respecter la Loi 25 du Québec, Forfeo utilise Certn. Le propriétaire paie l'enquête, mais un lien sécurisé est envoyé au locataire pour qu'il prenne un selfie et donne son consentement formel avant la vérification chez Equifax.</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-black text-slate-900 mb-2">Comment sont comptabilisées mes dépenses ?</h4>
                    <p className="text-slate-600 text-sm">Lorsque vous acceptez le devis d'un artisan "Forfeo Pro" depuis le Centre de Maintenance, la facture (incluant TPS/TVQ) est automatiquement consignée dans l'onglet Finances. Vous pouvez générer un rapport PDF pour votre relevé fiscal annuel qui s'archive à vie.</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-black text-slate-900 mb-2">Comment les locataires paient-ils le loyer ?</h4>
                    <p className="text-slate-600 text-sm">Vous devez configurer Stripe Connect dans l'onglet "Finances". Une fois fait, le locataire aura un bouton "Payer mon loyer" sur son propre tableau de bord. L'argent est déposé sur votre compte bancaire en 2 jours ouvrables.</p>
                  </div>
                </div>
             </div>
          )}

        </div>
      </main>

      {showProTicketModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden border border-slate-200">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-purple-50">
              <div>
                <h3 className="text-2xl font-black text-slate-900 flex items-center gap-2"><Briefcase size={24} className="text-purple-600"/> Appel d'Offres Pro</h3>
                <p className="text-sm font-bold text-purple-600 mt-1">Demandez un devis aux artisans Forfeo.</p>
              </div>
              <button onClick={() => setShowProTicketModal(false)} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-900 shadow-sm border border-purple-100"><X size={20}/></button>
            </div>
            
            <form onSubmit={submitProTicket} className="p-8 space-y-6">
              {myListings.length === 0 ? (
                <div className="bg-amber-50 p-4 rounded-xl text-amber-800 text-sm font-bold">
                  Vous devez avoir au moins un immeuble/logement enregistré pour demander un devis.
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Immeuble / Logement concerné</label>
                    <select 
                      value={proTicketForm.listing_id} 
                      onChange={e => setProTicketForm({...proTicketForm, listing_id: e.target.value})} 
                      className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-purple-500 outline-none"
                    >
                      {myListings.map(l => (
                        <option key={l.id} value={l.id}>{l.title} - {l.address}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Corps de métier</label>
                      <select 
                        value={proTicketForm.category} 
                        onChange={e => setProTicketForm({...proTicketForm, category: e.target.value})} 
                        className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer"
                      >
                        <option value="plomberie">Plomberie</option>
                        <option value="electricite">Électricité</option>
                        <option value="chauffage">Chauffage / Clim</option>
                        <option value="menage">Entretien Ménager</option>
                        <option value="renovation">Rénovation / Menuiserie</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Délai souhaité</label>
                      <select 
                        value={proTicketForm.urgency} 
                        onChange={e => setProTicketForm({...proTicketForm, urgency: e.target.value})} 
                        className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer"
                      >
                        <option value="basse">Pas pressé</option>
                        <option value="normale">Normal (Cette semaine)</option>
                        <option value="urgente">Urgent 🚨</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Détails de la demande</label>
                    <textarea 
                      required 
                      placeholder="Ex: J'aimerais refaire la salle de bain du 2ème étage..."
                      value={proTicketForm.description} 
                      onChange={e => setProTicketForm({...proTicketForm, description: e.target.value})} 
                      className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-purple-500 outline-none h-32 resize-none" 
                    />
                  </div>
                  
                  <button type="submit" disabled={isSubmittingProTicket} className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-purple-600 transition-colors shadow-lg flex items-center justify-center gap-2">
                    {isSubmittingProTicket ? 'Envoi...' : 'Soumettre aux artisans'}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
